import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { OrganizationProfileImage } from '@/components/OrganizationProfileImage';
import { logError } from '@/imports/logging/ClientLogger';
import { sponsor_lead_submitted } from '@/analytics';
import { IRequestDemoView } from '@/types/analytics';
import { REG_EXP } from '@/utilities';

import {
  RequestDemoModal_CreateOrganizationLeadMutation,
  RequestDemoModal_CreateOrganizationLeadMutationVariables,
} from './graphql-types/RequestDemoModal_CreateOrganizationLeadMutation';
import { RequestDemoModal_CurrentUserQuery } from './graphql-types/RequestDemoModal_CurrentUserQuery';
import { RequestDemoModal_OrganizationQuery } from './graphql-types/RequestDemoModal_OrganizationQuery';

type FormInput = {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
  company?: string;
  primaryPhone?: string;
};

const ORGANIZATION_QUERY = gql`
  query RequestDemoModal_OrganizationQuery($input: SingleOrganizationInput!) {
    organization(input: $input) {
      result {
        _id
        displayName
        sponsorship {
          currentTier {
            analyticsTierType
          }
        }
        ...OrganizationProfileImage_OrganizationFragment
      }
    }
  }
  ${OrganizationProfileImage.organizationFragment}
`;

const CURRENT_USER_QUERY = gql`
  query RequestDemoModal_CurrentUserQuery {
    currentUser {
      _id
      firstName
      lastName
      email
      primaryPhone
      jobTitle
      company
    }
  }
`;

const CREATE_ORGANIZATION_LEAD_MUTATION = gql`
  mutation RequestDemoModal_CreateOrganizationLeadMutation(
    $input: CreateOrganizationLeadInput!
  ) {
    createOrganizationLead(input: $input) {
      data {
        _id
      }
    }
  }
`;

type IRequestDemoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  registerInterestTitle: string;

  origin: {
    conferenceBoothId?: string;
    sponsoredEventId?: string;
    sponsoredConferenceSessionId?: string;
    hostedEventId?: string;
    sponsorPage?: boolean;
    featuredSponsorCard?: boolean;
  };

  analytics: {
    view: IRequestDemoView;
  };
};

export const RequestDemoModal: React.FC<IRequestDemoModalProps> = (props) => {
  const organization = useQuery<RequestDemoModal_OrganizationQuery>(
    ORGANIZATION_QUERY,
    { variables: { input: { id: props.organizationId } } }
  );

  const [isConsent, setConsent] = useState(false);

  const currentUser =
    useQuery<RequestDemoModal_CurrentUserQuery>(CURRENT_USER_QUERY);

  const [createOrganizationLead, createOrganizationLeadResult] = useMutation<
    RequestDemoModal_CreateOrganizationLeadMutation,
    RequestDemoModal_CreateOrganizationLeadMutationVariables
  >(CREATE_ORGANIZATION_LEAD_MUTATION);

  const toast = useToast();

  const {
    register,
    formState: { errors },
    handleSubmit,
    formState,
    reset,
  } = useForm<FormInput>();

  React.useEffect(() => {
    reset({
      firstName: currentUser.data?.currentUser?.firstName,
      lastName: currentUser.data?.currentUser?.lastName,
      email: currentUser.data?.currentUser?.email || undefined,
      jobTitle: currentUser.data?.currentUser?.jobTitle || undefined,
      company: currentUser.data?.currentUser?.company || undefined,
      primaryPhone: currentUser.data?.currentUser?.primaryPhone || undefined,
    });
  }, [currentUser.data, reset]);

  if (organization.loading) {
    return null;
  }

  if (currentUser.loading) {
    return null;
  }

  if (organization.error) {
    logError(organization.error);
    return null;
  }

  if (currentUser.error) {
    logError(currentUser.error);
    return null;
  }

  const onSubmit = async (data: FormInput): Promise<void> => {
    try {
      await createOrganizationLead({
        variables: {
          input: {
            data: {
              dateCreated: new Date().toISOString(),
              createdByUserId: currentUser.data?.currentUser?._id || '',
              organizationId: props.organizationId,
              displayName: `${data.firstName} ${data.lastName}`,
              jobTitle: data.jobTitle,
              company: data.company,
              email: data.email,
              primaryPhone: data.primaryPhone,
              origin: props.origin,
            },
          },
        },
      });

      sponsor_lead_submitted({
        entity: {
          id: props.organizationId,
          type: 'organization_lead',
        },
        sponsor: {
          id: props.organizationId,
          name: organization.data?.organization?.result?.displayName || '',
        },
        sponsor_id: props.organizationId,
        sponsor_name: organization.data?.organization?.result?.displayName || '',
        sponsor_tier:
          organization.data?.organization?.result?.sponsorship?.currentTier
            ?.analyticsTierType || undefined,
        sponsor_interaction_source: props.origin.sponsorPage
          ? 'sponsor_detail'
          : props.origin.sponsoredEventId ||
              props.origin.hostedEventId ||
              props.origin.conferenceBoothId ||
              props.origin.sponsoredConferenceSessionId
            ? 'event'
            : props.origin.featuredSponsorCard
              ? 'featured_sponsor'
              : props.analytics.view,
        event_backed_sponsor_interaction: Boolean(
          props.origin.sponsoredEventId ||
            props.origin.hostedEventId ||
            props.origin.conferenceBoothId ||
            props.origin.sponsoredConferenceSessionId
        ),
        event_sponsor_id: props.organizationId,
        event_id:
          props.origin.sponsoredEventId || props.origin.hostedEventId || undefined,
        event_session_id:
          props.origin.conferenceBoothId ||
          props.origin.sponsoredConferenceSessionId ||
          undefined,
        is_sponsor_interaction: true,
        lead: {
          requestType: 'demo',
          ctaText: props.registerInterestTitle,
        },
      });

      toast({
        description: 'Successfully requested a demo.',
        status: 'success',
      });

      props.onClose();
    } catch (err) {
      toast({
        description: 'Failed to request a demo.',
        status: 'error',
      });
      logError(err);
      props.onClose();
    }
  };
  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConsent(e.target.checked);
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      size={{ base: 'full', md: 'xl'}}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader
            aria-label={props.registerInterestTitle}
            display="flex"
            flexDirection="column"
            alignItems="center"
            pb="1"
          >
            {organization.data?.organization?.result && (
              <OrganizationProfileImage
                mb="4"
                organization={organization.data.organization.result}
              />
            )}
            {props.registerInterestTitle}
          </ModalHeader>
          <ModalBody pt="1" px="6" pb="6">
            <Text color="gray.600" textAlign="center" mb="5">
              This form will share your contact information with{' '}
              {organization.data?.organization?.result?.displayName} so they can
              get in touch with you.
            </Text>
            <SimpleGrid columns={2} spacing="4" marginBottom={2}>
              <FormControl isRequired isInvalid={!!errors?.firstName?.message}>
                <FormLabel>First Name</FormLabel>
                <Input
                  autoFocus
                  {...register('firstName', {
                    required: 'First name is required',
                  })}
                />
                <FormErrorMessage>
                  {errors?.firstName?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isRequired isInvalid={!!errors?.lastName?.message}>
                <FormLabel>Last Name</FormLabel>
                <Input
                  {...register('lastName', {
                    required: 'Last name is required',
                  })}
                />
                <FormErrorMessage>{errors?.lastName?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isRequired isInvalid={!!errors?.email?.message}>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: REG_EXP.EmailWithTLD,
                      message: 'Email must be valid',
                    },
                  })}
                />
                <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors?.primaryPhone?.message}>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  type="tel"
                  {...register('primaryPhone', {
                    pattern: {
                      value: REG_EXP.Phone,
                      message: 'Phone number must be valid',
                    },
                  })}
                />
                <FormErrorMessage>
                  {errors?.primaryPhone?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors?.company?.message}>
                <FormLabel>Company</FormLabel>
                <Input {...register('company')} />
                <FormErrorMessage>{errors?.company?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors?.jobTitle?.message}>
                <FormLabel>Job title</FormLabel>
                <Input {...register('jobTitle')} />
                <FormErrorMessage>{errors?.jobTitle?.message}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>
            <FormControl>
              <FormLabel>We Value Your Privacy</FormLabel>
              <Flex gap={4} align={'end'} direction={'column'}>
                <Text>
                  By checking “I Agree”, you consent to sharing your contact
                  details (name, email, phone number) with the specified
                  sponsor(s). They may use this information to provide you with
                  updates, offers, and service-related communication. Your data
                  will only be shared with these sponsor(s) you follow.
                </Text>
                <Checkbox isChecked={isConsent} onChange={handleConsentChange}>
                  I Agree
                </Checkbox>
              </Flex>
            </FormControl>
          </ModalBody>
          <ModalFooter borderTop="1px" borderTopColor="gray.100" display={'flex'} justifyContent={'space-between'}>
            <Button variant="outline" onClick={props.onClose} >
              Close
            </Button>
            <Button
              isLoading={createOrganizationLeadResult.loading}
              isDisabled={
                (formState.isSubmitted && !formState.isValid) || !isConsent
              }
              type="submit"
              id="requestDemoSubmitButton"
            >
              {props.registerInterestTitle}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
